import { useEffect, useState } from 'react';
import { EU_VISIBILITY_SLUG } from '@/features/common/layout/publicPageLinks';
import { isBlankHtml } from '@/features/common/richText';
import type { EuVisibilityContent } from '@/features/common/types/EuVisibilityContent';
import { contentApi } from '@/services/contentApi';
import { publicLayout } from '@/i18n/dictionaries/publicLayout';
import { useLanguage } from '@/i18n/LanguageContext';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/common/layout/footer.css';

/**
 * EU funding identity & visibility strip (FR-041).
 *
 * The text is administrable content, not a fixed string: an editor maintains it as the PAGE with
 * slug `eu-visibility`, and the bundled wording is only what shows before anyone has written it.
 *
 * The page's TITLE is the funding statement set beside the emblem („Finanțat de Uniunea
 * Europeană”), which is where the EU visibility rules put it; the Thymeleaf fragment read the
 * title too. The BODY is the sentence under it, and is rendered as the HTML it is — it was put in
 * a <p> as text, so an administrator's paragraph showed its tags (T143). It is safe to insert for
 * the reason ContentPage gives: every body is stored through the backend's HtmlSanitizer.
 *
 * A missing page is the normal state on a fresh install, not an error, so the 404 is swallowed
 * and the default stands. Anything else — a 500, a network failure — also falls back rather than
 * blanking the strip: FR-041 is a visibility obligation, and an empty strip fails it in a way a
 * slightly stale sentence does not.
 *
 * The flag stays an <img>. It is the EU emblem, whose blue and gold are the point; a sprite
 * symbol paints in one inherited colour and would turn it into a monochrome silhouette.
 */
export function EuStrip() {
  const { t } = useTranslation(publicLayout);
  const { language } = useLanguage();
  const [content, setContent] = useState<EuVisibilityContent | null>(null);

  useEffect(() => {
    let live = true;
    contentApi
      .publicPage<EuVisibilityContent>(EU_VISIBILITY_SLUG)
      .then((page) => {
        if (live) setContent(page);
      })
      .catch(() => {
        if (live) setContent(null);
      });
    return () => {
      live = false;
    };
  }, [language]);

  return (
    <div className="eu-strip">
      <img className="eu-strip__flag" src="/img/icons/eu.svg" alt="" aria-hidden="true" />
      {content ? (
        <div className="eu-strip__text">
          <p className="eu-strip__statement">{content.title}</p>
          {!isBlankHtml(content.body) && (
            // HTML SINK — source: the stored body of the EU-visibility site page · written by: a
            // holder of `content.manage`, through the pages screen · sanitised by: the backend
            // HtmlSanitizer, on the write path.
            <div
              className="eu-strip__body"
              dangerouslySetInnerHTML={{ __html: content.body ?? '' }}
            />
          )}
        </div>
      ) : (
        <p className="eu-strip__text">{t('euStripDefault')}</p>
      )}
    </div>
  );
}
